'use client';
import Link from 'next/link';
import Button from '@/components/shared/Button';
import Image from '@/components/shared/Image';
import { HtmlBlock } from '@/components/ui/HtmlBlock';
import { sheetStore } from '@/components/ui/ProductLayout/stores/sheetStore';
import { Media } from '@/graphql/common/types';
import { ActionBlock, FileBlock, SheetBlock } from '@/graphql/queries/pages/compromisso-asg/types';
import { cn } from '@/utils/mergeClassName';
import { onlyTextTags } from '@/utils/onlyTextTags';

type DescriptionBannerProps = {
  title: string;
  description: string;
  action?: ActionBlock;
  file?: FileBlock;
  sheet?: SheetBlock;
  media: Media;
  enableBackgroundColor?: boolean;
  className?: string;
};

export const DescriptionBanner = ({
  title,
  description,
  action,
  file,
  sheet,
  media,
  enableBackgroundColor = false,
  className
}: DescriptionBannerProps) => {
  const setOpenSheetEsgDescription = sheetStore((state) => state.setOpenEsgDescription);

  return (
    <section
      className={cn(
        'w-full px-6 py-12 lg:px-20 lg:py-[6.25rem] 2xl:px-0',
        enableBackgroundColor ? 'bg-grey-200' : 'bg-white',
        className
      )}
    >
      <div
        className={cn(
          'flex max-w-7xl flex-col items-center justify-center gap-10 lg:flex-row-reverse lg:justify-between xl:px-20 2xl:mx-auto 3xl:max-w-[67.5rem] 3xl:gap-20 3xl:px-0',
          media ? 'lg:flex-row-reverse' : 'lg:flex-row'
        )}
      >
        {media && (
          <>
            {action && action.link ? (
              <Link
                href={action.link?.url ?? ''}
                target={action.link?.target && `_${action.link?.target}`}
                aria-label={action.link?.ariaLabel ?? ''}
                className="relative flex items-center justify-center"
              >
                <Image
                  src={media.url ?? ''}
                  alt={media.alternativeText ?? ''}
                  width={400}
                  height={400}
                  className="h-[8.75rem] w-full object-contain lg:size-80 2xl:size-[25rem]"
                />
              </Link>
            ) : (
              <div className="relative flex items-center justify-center">
                <Image
                  src={media.url ?? ''}
                  alt={media.alternativeText ?? ''}
                  width={400}
                  height={400}
                  className="h-[8.75rem] w-full object-contain lg:size-80 2xl:size-[25rem]"
                />
              </div>
            )}
          </>
        )}
        <div className="flex flex-col justify-center gap-4 text-center lg:basis-[28.125rem] lg:text-left xl:basis-[31.25rem] 2xl:basis-[37.5rem]">
          <h2 className="font-publico text-2xl font-bold lg:text-[2rem] lg:leading-10 2xl:text-[2.5rem] 2xl:leading-[3rem]">
            {title}
          </h2>
          <HtmlBlock
            html={onlyTextTags(description)}
            className="text-base text-grey-700 2xl:text-xl"
          />
          <div className="mt-2 flex flex-col items-center justify-center gap-4 lg:mt-6 lg:items-start">
            {action && action.link && (
              <Button asChild outline={action.outline}>
                <Link
                  href={action.link?.url ?? ''}
                  target={action.link?.target && `_${action.link?.target}`}
                  aria-label={action.link?.ariaLabel ?? ''}
                >
                  {action.link?.label ?? ''}
                </Link>
              </Button>
            )}

            {file && file.document && (
              <Button asChild>
                <Link href={file.document?.url ?? ''} target="_blank" download>
                  {file?.label ?? ''}
                </Link>
              </Button>
            )}

            {sheet && sheet.openSheet && (
              <Button className="text-white" onClick={() => setOpenSheetEsgDescription(true)}>
                {sheet.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
